/**
 * Storage error classification + safe-persist
 * helper.
 *
 * The audit flagged eight call
 * sites in the codebase that silently swallow `QuotaExceededError`
 * and similar `DOMException`s from localStorage / sessionStorage /
 * IndexedDB. The user toggles a bookmark, saves a view, or runs an
 * import: the in-memory React state shows it took effect, but on
 * reload the value reverts because the write failed.
 *
 * This module centralises the classification + the one-time-per-
 * session toast surface so every storage call site can `safePersist`
 * (web storage) or `withStorageWrite` (IndexedDB) without re-wiring
 * its own toast logic.
 *
 * Conventions:
 *  - `classifyStorageError` returns a tagged union so callers can
 *  dispatch on `kind` rather than poking at `DOMException.name`.
 *  - `safePersist` returns a `StorageWriteResult` so callers can
 *  update React state conditionally (e.g. snap back to the
 *  previous value when the write failed catastrophically).
 *  - `registerStorageToastEmitter` is the bridge between this module
 *  (plain functions, so no `useToast`) and the React tree. The toast
 *  host in `src/next/Toast.tsx` registers on mount and MUST pass
 *  `null` on unmount: the emitter is module-level state, and a
 *  retained callback would write into an unmounted tree.
 */

import { errorLog } from "./errorLog";

/** Per-session toast budget. Stops every keystroke spamming the bar. */
const TOAST_BUDGET_PER_KIND_MS = 30_000;
const lastToastAt: Partial<Record<StorageErrorKind, number>> = {};

/** Tagged classification of every storage write/read failure. */
export type StorageErrorKind =
  | "quota-exceeded"
  | "blocked-by-other-tab"
  | "access-denied"
  | "corrupted"
  | "unknown";

export interface ClassifiedStorageError {
  kind: StorageErrorKind;
  /** Original error retained so the caller can log it. */
  raw: unknown;
}

/**
 * Classify a thrown value into one of our well-known buckets. Tolerant
 * of non-Error inputs (strings, plain objects) because some browsers
 * stringify their errors before they bubble.
 */
export function classifyStorageError(err: unknown): ClassifiedStorageError {
  // DOMException with a recognisable name. The most common case.
  if (err instanceof DOMException) {
    if (err.name === "QuotaExceededError" || err.code === 22 || err.code === 1014) {
      return { kind: "quota-exceeded", raw: err };
    }
    if (err.name === "SecurityError") {
      return { kind: "access-denied", raw: err };
    }
    if (err.name === "InvalidStateError" || err.name === "InvalidAccessError") {
      return { kind: "blocked-by-other-tab", raw: err };
    }
    if (err.name === "DataError" || err.name === "DataCloneError") {
      return { kind: "corrupted", raw: err };
    }
  }
  // Some browsers throw plain Error or stringly-typed values. Match on
  // the message so the import-runner's QuotaExceeded surfaces even when
  // a polyfill loses the DOMException shape.
  const message = err instanceof Error ? err.message : String(err ?? "");
  if (/quota.*exceed/i.test(message) || /storage.*full/i.test(message)) {
    return { kind: "quota-exceeded", raw: err };
  }
  if (/blocked.*tab/i.test(message) || /version.*conflict/i.test(message)) {
    return { kind: "blocked-by-other-tab", raw: err };
  }
  return { kind: "unknown", raw: err };
}

/** Type guard sugar around {@link classifyStorageError}. */
export function isQuotaExceeded(err: unknown): boolean {
  return classifyStorageError(err).kind === "quota-exceeded";
}

/**
 * Bridge surface — every storage call site forwards classified errors
 * here. The React tree registers a callback via
 * {@link registerStorageToastEmitter}; tests / non-React contexts
 * leave it `null` and the helper silently degrades.
 */
export interface StorageToastEmitter {
  /**
   * Show a toast for a storage failure of the given kind. The bridge
   * looks up localised copy + decides whether to render an action
   * (e.g. "Open Workspace storage" for quota-exceeded).
   */
  (kind: StorageErrorKind, hint?: string): void;
}

let emitter: StorageToastEmitter | null = null;

/** React tree calls this on mount. Idempotent re-register is safe. */
export function registerStorageToastEmitter(next: StorageToastEmitter | null): void {
  emitter = next;
}

/**
 * Push a storage-failure toast, debounced to once per
 * {@link TOAST_BUDGET_PER_KIND_MS} milliseconds per kind. Returns
 * `true` when the toast was emitted, `false` when it was suppressed
 * (no emitter registered, or within budget window).
 */
export function emitStorageToast(kind: StorageErrorKind, hint?: string): boolean {
  if (!emitter) return false;
  const now = Date.now();
  const previous = lastToastAt[kind] ?? 0;
  if (now - previous < TOAST_BUDGET_PER_KIND_MS) return false;
  lastToastAt[kind] = now;
  emitter(kind, hint);
  return true;
}

/** Reset the debounce budget. Test-only. */
export function __resetStorageToastBudget(): void {
  for (const k of Object.keys(lastToastAt) as StorageErrorKind[]) {
    delete lastToastAt[k];
  }
}

/** Result of a guarded storage write. */
export interface StorageWriteResult {
  ok: boolean;
  error?: ClassifiedStorageError;
}

/**
 * Write a JSON-encodable value to web storage with classified error
 * handling. Returns `{ ok: false, error }` on failure so callers can
 * surface follow-up UI (e.g. snap React state back). On
 * quota-exceeded, also calls {@link emitStorageToast} so the user
 * sees a toast at most once per 30 s per kind.
 *
 * Out-of-band storage access (private-mode Safari, SSR, sandboxed
 * iframes) returns `{ ok: false }` with no toast — that's the
 * "storage is intentionally unavailable" path, not a quota failure.
 */
export function safePersist(
  key: string,
  value: string,
  storage: "local" | "session" = "local",
): StorageWriteResult {
  if (typeof window === "undefined") return { ok: false };
  let storageRef: Storage;
  try {
    storageRef = storage === "session" ? window.sessionStorage : window.localStorage;
  } catch (err) {
    errorLog("safePersist.access", err, { key, storage });
    return { ok: false, error: classifyStorageError(err) };
  }
  try {
    storageRef.setItem(key, value);
    return { ok: true };
  } catch (err) {
    const classified = classifyStorageError(err);
    errorLog("safePersist.write", err, { key, storage, kind: classified.kind });
    if (classified.kind === "quota-exceeded") {
      emitStorageToast("quota-exceeded", key);
    } else if (classified.kind === "access-denied") {
      emitStorageToast("access-denied", key);
    }
    return { ok: false, error: classified };
  }
}

/**
 * Wrap an IndexedDB write with quota classification. The transaction-
 * level error / abort handlers fire async, so the helper observes the
 * underlying `IDBRequest` rather than the synchronous put call.
 *
 * Callers pass an async function that performs the put. On
 * quota-exceeded, the helper emits the toast and re-throws so the
 * import-runner can mark the artifact `failed` with a useful reason.
 */
export async function withStorageWrite<T>(
  op: () => Promise<T>,
  context: string,
): Promise<T> {
  try {
    return await op();
  } catch (err) {
    const classified = classifyStorageError(err);
    errorLog(`storage.${context}`, err, { kind: classified.kind });
    if (classified.kind === "quota-exceeded") {
      emitStorageToast("quota-exceeded", context);
    } else if (classified.kind === "blocked-by-other-tab") {
      emitStorageToast("blocked-by-other-tab", context);
    }
    throw err;
  }
}
